import { compactInputClass, insetSurfaceCardClass, inputClass, secondaryBtnClass, secondarySurfaceClass } from "@/components/common/surfaceClasses";
import { TrainingContactResultCard } from "@/components/lead/sections/TrainingContactResultCard";
import type { ScopeFilter, SourceFilter, TrainingContactsPanelProps } from "@/components/lead/sections/trainingContactsDirectoryShared";
import { useTrainingContactsDirectory } from "@/components/lead/sections/useTrainingContactsDirectory";

export function TrainingContactsPanel(props: TrainingContactsPanelProps) {
  const d = useTrainingContactsDirectory(props);

  return (
    <div className={`${secondarySurfaceClass} p-4 space-y-3`}>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 mb-1">Search contacts</div>
          <input
            className={`w-full ${inputClass} rounded-xl`}
            value={d.query}
            placeholder='e.g., "paperwork", "facebook ads", "who handles warranty"'
            onChange={(e) => d.setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void d.runSearch(d.query, d.locationScope);
              }
            }}
          />
        </div>
        <button
          type="button"
          className={
            "px-4 py-2 rounded-xl text-sm font-semibold " +
            (d.loading ? "bg-gray-300 text-gray-600" : "bg-indigo-600 text-white hover:bg-indigo-700")
          }
          disabled={d.loading}
          onClick={() => void d.runSearch(d.query, d.locationScope)}
        >
          {d.loading ? "Loading…" : "Search"}
        </button>
        {d.canUseTrustTestingControls && d.trustOverride !== "auto" ? (
          <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
            TEST MODE: Trust override active
          </span>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <label className="text-xs text-gray-600 dark:text-zinc-400">
          Scope
          <select
            className={`ml-1 ${compactInputClass} text-xs`}
            value={d.scopeFilter}
            onChange={(e) => d.setScopeFilter(e.target.value as ScopeFilter)}
          >
            <option value="all">All</option>
            <option value="org">Org</option>
            <option value="manufacturer">Manufacturer</option>
            <option value="platform">Platform</option>
          </select>
        </label>

        <label className="text-xs text-gray-600 dark:text-zinc-400">
          Source
          <select
            className={`ml-1 ${compactInputClass} text-xs`}
            value={d.sourceFilter}
            onChange={(e) => d.setSourceFilter(e.target.value as SourceFilter)}
          >
            <option value="all">All</option>
            <option value="user">App users</option>
            <option value="external">External</option>
          </select>
        </label>

        <label className="text-xs text-gray-600 dark:text-zinc-400">
          Location
          <select
            className={`ml-1 ${compactInputClass} text-xs`}
            value={d.locationScope}
            onChange={(e) => {
              const next = e.target.value as typeof d.locationScope;
              d.setLocationScope(next);
              void d.runSearch(d.query, next);
            }}
          >
            <option value="current" disabled={!d.locationId}>
              {d.locationId ? `Current (${d.locationName || "selected"})` : "Current"}
            </option>
            <option value="all">All org locations</option>
          </select>
        </label>

        {d.auth.isAdmin ? (
          <label className="text-xs text-gray-600 inline-flex items-center gap-1">
            <input type="checkbox" checked={d.directOnly} onChange={(e) => d.setDirectOnly(e.target.checked)} />
            Direct contact info only
          </label>
        ) : null}

        {d.canUseTrustTestingControls ? (
          <label className="text-xs text-gray-600 dark:text-zinc-400">
            Trust override (testing)
            <select
              className={`ml-1 ${compactInputClass} text-xs`}
              value={d.trustOverride}
              onChange={(e) => {
                const next = e.target.value as typeof d.trustOverride;
                d.setTrustOverride(next);
                void d.runSearch(d.query, d.locationScope, next);
              }}
            >
              <option value="auto">Auto (from roles)</option>
              <option value="1">Force Level 1</option>
              <option value="2">Force Level 2</option>
              <option value="3">Force Level 3</option>
            </select>
          </label>
        ) : null}
      </div>

      {d.canUseTrustTestingControls && d.trustDebug ? (
        <div className={`text-[11px] text-gray-600 dark:text-zinc-400 ${insetSurfaceCardClass} px-2 py-1.5`}>
          Trust diagnostic: effective L{d.trustDebug.effectiveTrustLevel ?? "?"}
          {typeof d.trustDebug.computedTrustLevel === "number" ? ` • computed L${d.trustDebug.computedTrustLevel}` : ""}
          {typeof d.trustDebug.requestedTrustOverride === "number"
            ? ` • requested override L${d.trustDebug.requestedTrustOverride}`
            : ""}
          {d.trustOverride !== "auto" && d.trustDebug.appliedTrustOverride === false
            ? " • override not applied (platform role required)"
            : ""}
        </div>
      ) : null}

      <div className="text-xs text-gray-500">
        Showing {d.filteredContacts.length} of {d.contacts.length} loaded contact rows
        {d.lastSearch ? ` for "${d.lastSearch}"` : " (full list view)"}.
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="text-xs text-gray-600 inline-flex items-center gap-1">
          Rows
          <select
            className={`${compactInputClass} px-1.5 py-1 text-xs`}
            value={d.visibleRows}
            onChange={(e) => {
              d.setVisibleRows(Number(e.target.value) as 5 | 10 | 20);
              d.setCurrentPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
        </label>
        <div className="text-xs text-gray-600 inline-flex items-center gap-2">
          <button
            type="button"
            className={`${secondaryBtnClass} px-2 py-1 disabled:opacity-50`}
            disabled={d.currentPage <= 1}
            onClick={() => d.setCurrentPage((p) => Math.max(1, p - 1))}
          >
            ← Prev
          </button>
          <span>
            Page {d.currentPage} / {d.totalPages}
          </span>
          <button
            type="button"
            className={`${secondaryBtnClass} px-2 py-1 disabled:opacity-50`}
            disabled={d.currentPage >= d.totalPages}
            onClick={() => d.setCurrentPage((p) => Math.min(d.totalPages, p + 1))}
          >
            Next →
          </button>
        </div>
      </div>

      {d.error ? (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl p-2 dark:bg-red-950/40 dark:border-red-800 dark:text-red-300">
          Could not load contacts: {d.error}
        </div>
      ) : null}

      {!d.error && d.filteredContacts.length === 0 ? (
        <div className="text-xs text-gray-500">No contacts match this filter set.</div>
      ) : null}

      {d.filteredContacts.length > 0 ? (
        <div className="space-y-2">
          {d.pagedContacts.map((c, i) => (
            <TrainingContactResultCard
              key={`${c.tab}-${c.source || "unknown"}-${c.sourceId ?? `p${i}`}`}
              c={c}
              organizationId={d.organizationId}
              logQuery={d.lastSearch || d.query.trim()}
              recommended={d.recommended}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
